import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Contact from "../components/Contact";
import Breadcrumb from "../components/Breadcrumb";
import MarineSparesAdvantages from "../components/MarineSparesAdvantages";
import useSEO from "../hooks/useSEO";
import { SITE } from "../constants/siteConfig";
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS, TRANSITIONS, HERO_HEIGHTS } from "../constants/designTokens";

const subItems = [
  "15ppm Bilge Alarms & Content Monitors",
  "Oil Content Meters & Measuring Cells",
  "Solenoid Valves, Sample Pumps & Filters",
  "DP Transmitters & Flow Meters for ODME",
  "Replacement Coalescers & Filter Elements",
  "Main Logic Boards & Display Modules"
];

const faqs = [
  {
    question: "How often should a 15ppm bilge alarm be calibrated?",
    answer:
      "Most classification societies and manufacturers recommend calibrating 15ppm bilge alarms and oil content monitors annually, or per the maker's maintenance schedule. Regular calibration ensures the alarm accurately detects oil content above the MARPOL threshold and keeps your Oil Record Book entries and IOPP Certificate valid during class surveys."
  },
  {
    question: "How do you ensure OWS and ODME spares meet MARPOL compliance?",
    answer:
      "All OWS and ODME spares we supply are sourced from manufacturers holding valid type approval under MARPOL Annex I (Regulations 14 and 31), with certificates of conformity and test documentation provided. We work directly with brands such as Deckma Hamburg, Rivertrace, and JOWA to ensure every component matches the original type-approved specification for your Oily Water Separator or Oil Discharge Monitoring Equipment."
  },
  {
    question: "How do I know when to replace an oil content meter measuring cell?",
    answer:
      "Measuring cells should be replaced when calibration drift can no longer be corrected, when readings become inconsistent or unstable, or per the manufacturer's recommended service interval (commonly every 1-2 years depending on usage). Signs like frequent false alarms, cloudy or scratched cell windows, or failed calibration checks are strong indicators it's time for a replacement cell."
  },
  {
    question: "Can you source spares for obsolete or discontinued OWS controllers?",
    answer:
      "Yes. Many vessels still operate older Deckma, Rivertrace, or JOWA controller units that have since been discontinued. Our procurement network sources original, refurbished, and compatible replacement boards, sensors, and display modules for legacy OWS and ODME systems, helping you avoid a costly full system upgrade before it's necessary."
  },
  {
    question: "Do you provide documentation for class surveys and PSC inspections?",
    answer:
      "Every OWS and ODME spare part ships with full supporting documentation, including certificates of conformity, calibration certificates where applicable, and manufacturer datasheets. This paperwork is ready for class surveyors and Port State Control inspections, helping demonstrate continued MARPOL Annex I compliance without delays to your vessel's schedule."
  }
];

const OwsOdme = () => {
  useSEO({
    title: "OWS & ODME Spares",
    description:
      "OWS and ODME spare parts supporting strict MARPOL environmental compliance: 15ppm bilge alarms, oil content meters, measuring cells, solenoid valves and more from Deckma Hamburg, Rivertrace, and JOWA.",
    canonical: "/ows-odme",
    ogImage: "https://marine-sealink.com/img/port-aerial.jpg",
    breadcrumb: [
      { name: "Home", url: "/" },
      { name: "OWS & ODME Spares", url: "/ows-odme" }
    ],
    faqs
  });

  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = idx => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const whatsappHref = `${SITE.whatsapp}?text=${encodeURIComponent(
    "Hello, I need assistance with OWS & ODME spare parts."
  )}`;

  return (
    <div className="page-transition">
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "OWS & ODME Spares", href: "/ows-odme" }]} />
      <style>{`
        .ows-odme-hero {
          background: linear-gradient(135deg, rgba(8,32,50,0.85), rgba(64,168,196,0.75)), url('/img/port-aerial.jpg') center center/cover no-repeat;
        }

        .ows-odme-hero-card {
          background: rgba(8, 32, 50, 0.45);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 32px 0 rgba(8, 32, 50, 0.35);
        }

        .ows-odme-items {
          list-style: none;
          padding: 0;
          margin: ${SPACING.lg} 0;
        }

        .ows-odme-items li {
          padding: ${SPACING.sm} 0;
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.base};
          display: flex;
          align-items: center;
          gap: ${SPACING.sm};
          border-bottom: 1px solid ${COLORS.ui.border};
        }

        .ows-odme-items li:last-child {
          border-bottom: none;
        }

        .ows-odme-items li i {
          color: ${COLORS.primary.teal};
          font-size: 1.1rem;
          flex-shrink: 0;
        }

        .ows-odme-detail-card {
          background: ${COLORS.background.white};
          border-radius: ${RADIUS.xl};
          padding: ${SPACING['2xl']};
          box-shadow: ${SHADOWS.md};
          border: 1px solid ${COLORS.ui.border};
        }

        .ows-odme-brands-tag {
          background: ${COLORS.background.lightTeal};
          border-radius: ${RADIUS.lg};
          padding: ${SPACING.sm} ${SPACING.md};
          font-size: ${TYPOGRAPHY.fontSize.sm};
          color: ${COLORS.primary.dark};
          display: inline-block;
          margin-top: ${SPACING.md};
        }

        .ows-odme-brands-tag strong {
          color: ${COLORS.primary.teal};
        }

        .ows-odme-note {
          background: ${COLORS.background.lightBlue};
          border-left: 4px solid ${COLORS.primary.teal};
          border-radius: ${RADIUS.md};
          padding: ${SPACING.md} ${SPACING.lg};
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.sm};
          line-height: ${TYPOGRAPHY.lineHeight.relaxed};
          margin-top: ${SPACING.xl};
        }

        .ows-odme-cta {
          background: linear-gradient(135deg, ${COLORS.primary.dark} 0%, #1a3a4f 50%, ${COLORS.primary.teal} 100%);
          position: relative;
          overflow: hidden;
        }

        .ows-odme-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: ${SPACING.sm};
          padding: ${SPACING.md} ${SPACING.xl};
          border-radius: ${RADIUS.lg};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          font-size: ${TYPOGRAPHY.fontSize.base};
          text-decoration: none;
          transition: ${TRANSITIONS.all};
          border: 2px solid transparent;
        }

        .ows-odme-cta-btn-primary {
          background: ${COLORS.background.white};
          color: ${COLORS.primary.dark};
          box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2);
        }

        .ows-odme-cta-btn-primary:hover {
          background: ${COLORS.primary.teal};
          color: ${COLORS.text.white};
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(64, 168, 196, 0.4);
        }

        .ows-odme-cta-btn-whatsapp {
          background: ${COLORS.ui.whatsapp};
          color: ${COLORS.text.white};
          box-shadow: 0 4px 20px rgba(37, 211, 102, 0.3);
        }

        .ows-odme-cta-btn-whatsapp:hover {
          background: ${COLORS.ui.whatsappHover};
          color: ${COLORS.text.white};
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(37, 211, 102, 0.5);
        }

        .ows-odme-faq-card {
          border-radius: ${RADIUS.lg};
          box-shadow: ${SHADOWS.sm};
          margin-bottom: ${SPACING.md};
          overflow: hidden;
          border: 1px solid ${COLORS.ui.border};
          transition: all 0.3s ease;
        }

        .ows-odme-faq-card:hover {
          box-shadow: ${SHADOWS.md};
        }

        .ows-odme-faq-header {
          background: ${COLORS.background.lightBlue};
          cursor: pointer;
          padding: ${SPACING.lg} ${SPACING.xl};
          transition: all 0.3s ease;
        }

        .ows-odme-faq-header:hover {
          background: ${COLORS.background.lightTeal};
        }

        .ows-odme-faq-header h5 {
          color: ${COLORS.primary.teal};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          font-size: ${TYPOGRAPHY.fontSize.md};
          margin: 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: ${SPACING.md};
        }

        .ows-odme-faq-header h5 span {
          flex-shrink: 0;
          font-weight: ${TYPOGRAPHY.fontWeight.normal};
          font-size: 1.5rem;
          line-height: 1;
          transition: transform 0.3s ease;
        }

        .ows-odme-faq-card.open .ows-odme-faq-header h5 span {
          transform: rotate(45deg);
        }

        .ows-odme-faq-body {
          color: ${COLORS.text.primary};
          font-size: ${TYPOGRAPHY.fontSize.base};
          background: ${COLORS.background.lightTeal};
          padding: ${SPACING.lg} ${SPACING.xl};
          line-height: ${TYPOGRAPHY.lineHeight.relaxed};
          border-top: 1px solid rgba(64, 168, 196, 0.2);
        }

        @media (max-width: 767.98px) {
          .ows-odme-hero-card {
            max-width: 90% !important;
            padding: 2rem 1.5rem !important;
          }
          .ows-odme-hero h1 {
            font-size: 1.75rem !important;
          }
          .ows-odme-hero p {
            font-size: 1rem !important;
          }
          .ows-odme-detail-card {
            padding: 1.5rem !important;
          }
          .ows-odme-cta h2 {
            font-size: 1.5rem !important;
          }
          .ows-odme-cta-buttons {
            flex-direction: column !important;
            gap: 1rem !important;
          }
          .ows-odme-cta-btn {
            width: 100% !important;
            justify-content: center !important;
          }
          .ows-odme-faq-header {
            min-height: 60px;
            display: flex;
            align-items: center;
            padding: ${SPACING.md} ${SPACING.lg};
          }
          .ows-odme-faq-header h5 {
            font-size: 1rem !important;
            line-height: 1.4;
          }
          .ows-odme-faq-body {
            font-size: 0.95rem !important;
            padding: ${SPACING.md} ${SPACING.lg};
          }
        }

        @media (max-width: 480px) {
          .ows-odme-hero-card {
            padding: 1.5rem 1rem !important;
          }
          .ows-odme-faq-header h5 {
            font-size: 0.95rem !important;
          }
          .ows-odme-faq-body {
            font-size: 0.9rem !important;
          }
        }
      `}</style>

      <Navbar />

      {/* Hero Section */}
      <section
        className="ows-odme-hero d-flex align-items-center justify-content-center"
        style={{
          minHeight: HERO_HEIGHTS.page,
          position: "relative",
          color: COLORS.text.white,
          textAlign: "center"
        }}
      >
        <div
          className="ows-odme-hero-card"
          style={{
            padding: `${SPACING['2xl']} ${SPACING.xl}`,
            borderRadius: RADIUS['2xl'],
            maxWidth: "700px",
            margin: "0 auto"
          }}
        >
          <h1 style={{
            fontWeight: TYPOGRAPHY.fontWeight.bold,
            fontSize: TYPOGRAPHY.fontSize['3xl'],
            marginBottom: SPACING.lg,
            lineHeight: TYPOGRAPHY.lineHeight.tight
          }}>OWS & ODME Spares</h1>
          <p style={{
            fontSize: TYPOGRAPHY.fontSize.xl,
            lineHeight: TYPOGRAPHY.lineHeight.relaxed,
            marginBottom: 0
          }}>
            Certified spares for Oily Water Separators and Oil Discharge Monitoring Equipment, ensuring your vessel's strict compliance with MARPOL environmental regulations.
          </p>
        </div>
      </section>

      {/* Category Detail Section */}
      <section className="py-5" style={{ background: COLORS.background.light }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="ows-odme-detail-card fade-in-section">
                <h2 style={{
                  color: COLORS.primary.dark,
                  fontWeight: TYPOGRAPHY.fontWeight.bold,
                  fontSize: TYPOGRAPHY.fontSize['2xl'],
                  marginBottom: SPACING.md
                }}>
                  OWS & ODME Spares
                </h2>
                <p style={{
                  color: COLORS.text.primary,
                  fontSize: TYPOGRAPHY.fontSize.lg,
                  lineHeight: TYPOGRAPHY.lineHeight.relaxed
                }}>
                  Certified spares ensuring your vessel's strict compliance with MARPOL environmental regulations.
                </p>

                <ul className="ows-odme-items">
                  {subItems.map((item, idx) => (
                    <li key={idx}>
                      <i className="fa-solid fa-circle-check"></i>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="ows-odme-brands-tag">
                  <strong>Supported Brands:</strong> Deckma Hamburg, Rivertrace, JOWA
                </div>

                <div className="ows-odme-note">
                  <i className="fa-solid fa-shield-halved" style={{ marginRight: SPACING.sm, color: COLORS.primary.teal }}></i>
                  All spares are supplied to keep your Oily Water Separator and Oil Discharge Monitoring Equipment fully compliant with MARPOL Annex I, helping you pass class surveys and Port State Control inspections without delay.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ows-odme-cta py-5">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="text-center" style={{ color: COLORS.text.white }}>
            <h2 style={{
              fontWeight: TYPOGRAPHY.fontWeight.bold,
              fontSize: TYPOGRAPHY.fontSize['2xl'],
              marginBottom: SPACING.md
            }}>
              Need OWS or ODME Spares? Get a Quote Today
            </h2>
            <p style={{
              fontSize: TYPOGRAPHY.fontSize.lg,
              marginBottom: SPACING.xl,
              maxWidth: "600px",
              margin: "0 auto",
              opacity: 0.9,
              lineHeight: TYPOGRAPHY.lineHeight.relaxed,
              paddingBottom: SPACING.xl
            }}>
              From 15ppm bilge alarms to ODME measuring cells, our team sources certified spares to keep your vessel MARPOL compliant. Contact us for fast, reliable service.
            </p>
            <div className="ows-odme-cta-buttons d-flex justify-content-center gap-3 flex-wrap">
              <a href="#contact" className="ows-odme-cta-btn ows-odme-cta-btn-primary">
                <i className="fa-solid fa-file-invoice"></i>
                Request a Quote
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="ows-odme-cta-btn ows-odme-cta-btn-whatsapp"
              >
                <i className="fa-brands fa-whatsapp"></i>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="fade-in-section">
        <MarineSparesAdvantages />
      </div>

      {/* FAQ Section */}
      <section className="py-5" style={{ background: COLORS.background.white }}>
        <div className="container">
          <h2 style={{
            color: COLORS.primary.dark,
            fontWeight: TYPOGRAPHY.fontWeight.bold,
            textAlign: "center",
            marginBottom: SPACING.md
          }}>
            Frequently Asked Questions
          </h2>
          <p style={{
            color: COLORS.text.primary,
            fontSize: TYPOGRAPHY.fontSize.base,
            textAlign: "center",
            maxWidth: "600px",
            margin: `0 auto ${SPACING['2xl']}`
          }}>
            Find answers to common questions about our OWS and ODME spare parts and MARPOL compliance support.
          </p>
          <div className="accordion" id="owsOdmeFAQ">
            {faqs.map((faq, idx) => (
              <div
                className={`ows-odme-faq-card ${openIndex === idx ? "open" : ""}`}
                key={idx}
              >
                <div
                  className="ows-odme-faq-header"
                  onClick={() => toggleFAQ(idx)}
                >
                  <h5>
                    {faq.question}
                    <span>{openIndex === idx ? "−" : "+"}</span>
                  </h5>
                </div>
                {openIndex === idx && (
                  <div className="ows-odme-faq-body">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="fade-in-section">
        <Contact />
      </div>
    </div>
  );
};

export default OwsOdme;
